import type {
    CallCategory,
    CallCategoryCues,
    DeviceCues,
    DigitalCompetency,
    DigitalCompetencyCues,
    LocationCues,
    SentimentCues,
    SoftwareCues,
} from "@expert/shared-types";

import type { ShadowMessage } from "./types";

type CueType = (
    | CallCategoryCues
    | DeviceCues
    | DigitalCompetencyCues
    | LocationCues
    | SentimentCues
    | SoftwareCues
)["type"];

// Each allowlist is a Record over its shared-types union, so a value added there fails typecheck until it is
// listed here. Anything a backend sends outside these lists is reported as UNKNOWN / UNRECOGNIZED, never echoed.
const CUE_TYPES: Record<CueType, true> = {
    CALL_CATEGORY: true,
    DEVICE: true,
    DIGITAL_COMPETENCY: true,
    LOCATION_CITY: true,
    LOCATION_STATE: true,
    SENTIMENT: true,
    SOFTWARE: true,
};

const CALL_CATEGORY_NAMES: Record<CallCategory, true> = {
    "APPLICATION/SOFTWARE_TROUBLESHOOTING": true,
    BATTERY: true,
    BILLS_AND_PAYMENT: true,
    CARRIER_ACCOUNT_MANAGEMENT: true,
    CONNECTIVITY_AND_NETWORK: true,
    DEVICE_ACTIVATION: true,
    DEVICE_HARDWARE_TROUBLESHOOTING: true,
    INSURANCE_AND_CLAIMS: true,
    MESSAGING: true,
    NOT_ENOUGH_INFORMATION: true,
    PHONE_CALLS: true,
    SECURITY_AND_PRIVACY: true,
    STORAGE_AND_CONTENT_MANAGEMENT: true,
    VOICEMAIL: true,
    DEVICE_DAMAGE: true,
    DEVICE_ORDER_STATUS: true,
    DEVICE_REPAIR: true,
    DEVICE_REPLACEMENT: true,
    GENERAL_CUSTOMER_SUPPORT: true,
    ORDER_STATUS: true,
    SIM_AND_PUK_ISSUES: true,
    TECHNICAL_SUPPORT: true,
    ACCOUNT_MANAGEMENT: true,
    ALTERNATE_LANGUAGE_REQUEST: true,
    FEATURES_AND_BENEFITS: true,
    INSTALLATION_AND_SETUP: true,
    SUBSCRIPTION_MANAGEMENT: true,
    UNINSTALL_AND_REMOVAL: true,
    USAGE_AND_FUNCTIONALITY: true,
    VIRUSES_MALWARE_SPAM: true,
    VPN: true,
};

const SENTIMENT_NAMES: Record<SentimentCues["name"], true> = {
    neutral: true,
    satisfied: true,
    frustrated: true,
    impressed: true,
};

const DIGITAL_COMPETENCY_NAMES: Record<DigitalCompetency, true> = {
    "Tech savvy": true,
    "Tech savvy business owner": true,
    "Less tech savvy": true,
};

/**
 * Cue types whose `name` is a closed enum (never customer data), so a listed name is safe to emit.
 * Every other type (e.g. `LOCATION_CITY`, `DEVICE`) is reported by type only.
 */
const NAMED_CUE_NAMES: Partial<Record<CueType, Readonly<Record<string, true>>>> = {
    CALL_CATEGORY: CALL_CATEGORY_NAMES,
    DIGITAL_COMPETENCY: DIGITAL_COMPETENCY_NAMES,
    SENTIMENT: SENTIMENT_NAMES,
};

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === "object" && value !== null;

const isListed = (list: Readonly<Record<string, true>>, value: unknown): value is string =>
    typeof value === "string" && Object.hasOwn(list, value);

/**
 * PII-safe summary of the cues a websocket message carries, as `TYPE:NAME` for enum-named cue
 * types (e.g. `CALL_CATEGORY:DEVICE_ACTIVATION`) and `TYPE` alone for the rest. Reads
 * `body.suggestion.cues` (`message-suggestion`) and `body.cues` (`orchestrator-invoke-success`).
 *
 * Returns `undefined` when the message has no cues array, so unpaired (`*-only`) comparisons
 * still reveal what each backend sent even though their bodies are never diffed.
 */
export function summarizeCues(message: ShadowMessage): string[] | undefined {
    const body = message.body;
    if (!isRecord(body)) return undefined;

    const suggestion = body.suggestion;
    const cues = isRecord(suggestion) && Array.isArray(suggestion.cues) ? suggestion.cues : body.cues;
    if (!Array.isArray(cues)) return undefined;

    return cues.filter(isRecord).map((cue) => {
        if (!isListed(CUE_TYPES, cue.type)) return "UNKNOWN";
        const names = NAMED_CUE_NAMES[cue.type as CueType];
        if (!names) return cue.type;
        return `${cue.type}:${isListed(names, cue.name) ? cue.name : "UNRECOGNIZED"}`;
    });
}
